import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { startConvertJob, waitForConvertJob, getConvertJobResult, downloadConvertJob, cancelConvertJob, getConverterCapabilities, installConverterEngine, fetchImportSample, apiErrorMessage } from '../../lib/projectApi';
import { downloadBlob, safeFileName } from '../../lib/renderUtils';
import { fontStatuses, uploadUserFont, onFontsChanged } from '../../lib/fontLoader';
import { Upload, FileText, FileImage, FolderOpen, AlertTriangle, CheckCircle2, Loader2, Info, Type, Replace, RefreshCw, Cog, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const RESUME_KEY = 'wp.convertJob'; // the running / finished job to pick up when the panel is opened again
const fmtElapsed = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
/** Short reference code for a failed job — the owner's Conversion engine page shows the same code next to the event. */
const refCode = (jobId) => (jobId ? String(jobId).slice(0, 8).toUpperCase() : null);

/**
 * Publisher (.pub) import panel: drop zone -> conversion -> previews + warnings -> open / download.
 * Used inside the Backstage (File > Import) and the `import-pub` dialog.
 *
 * Conversions run as server-side jobs (backend/convert_jobs.py): the upload returns a job id at once and the panel
 * polls — a big publication takes minutes, longer than a reverse proxy lets one request run (Cloudflare: ~100 s,
 * which used to surface here as "did not return a complete response within the 120-second Proxy Read Timeout").
 * The job id is remembered, so leaving the panel and coming back picks the conversion up where it is.
 */
export const ImportPublisherPanel = ({ onOpened }) => {
  const { dispatch, openDialog } = useEditor();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(null); // 'import' | 'pdf' | 'png' | 'sample'
  const [progress, setProgress] = useState(0); // upload %
  const [job, setJob] = useState(null); // the running job's status document ({ status, step, message, progress, elapsed_seconds })
  const [elapsed, setElapsed] = useState(0);
  const abortRef = useRef(null); // stops the polling (Cancel / unmount)
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errorRef, setErrorRef] = useState(null); // the failed job's reference code (owner ▸ Conversion engine lists it)
  const [caps, setCaps] = useState(null);
  const [drag, setDrag] = useState(false);
  const [fontInfo, setFontInfo] = useState(null); // [{ family, status }] for the fonts the .pub uses
  const [fontBusy, setFontBusy] = useState(false);
  const fontInputRef = useRef(null);

  // Conversion engine: the host's LibreOffice, the app's own runtime, or its install progress (lo_runtime.py). While it
  // is being prepared the capabilities are polled so the progress bar moves and the buttons unlock by themselves.
  const engine = caps?.engine || null;
  const engineReady = !!(caps && caps.libreoffice);
  const engineInstalling = engine?.state === 'installing';
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const c = await getConverterCapabilities();
        if (cancelled) return;
        setCaps(c);
        if (c?.engine?.state === 'installing') timer = window.setTimeout(load, 2500);
      } catch {
        if (!cancelled) setCaps({ libreoffice: false, engine: { state: 'failed', error: 'The server could not be reached.' } });
      }
    };
    load();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [retrying]);
  const retryEngine = async () => {
    try { await installConverterEngine(); } catch (e) { toast.error(await apiErrorMessage(e, 'Could not restart the engine install.')); return; }
    setCaps((c) => ({ ...(c || {}), engine: { ...(c?.engine || {}), state: 'installing', message: 'Preparing the conversion engine…' } }));
    setRetrying((n) => !n);
  };
  /** A conversion that failed because the engine is (still) being prepared or broke: refresh the engine state so the
   *  banner + polling pick up again instead of leaving a stale "ready" view. */
  const noteEngineError = async (e) => {
    try {
      let data = e?.response?.data;
      if (data instanceof Blob) data = JSON.parse(await data.text());
      const code = data?.detail?.code;
      if (code === 'engine_preparing' || code === 'engine_failed' || code === 'missing_dependency') setRetrying((n) => !n);
    } catch { /* ignore */ }
  };
  // .odg files are read directly (no LibreOffice needed); .pub files need the engine
  const needsEngine = !file || !/\.odg$/i.test(file.name);
  const blocked = caps ? (!engineReady && needsEngine) : false;

  // Which of the publication's fonts are actually available here (system / My fonts / Google) and which will be substituted.
  useEffect(() => {
    const fonts = result?.fonts || [];
    if (!fonts.length) { setFontInfo(null); return undefined; }
    let cancelled = false;
    const check = () => fontStatuses(fonts).then((info) => { if (!cancelled) setFontInfo(info); });
    check();
    const off = onFontsChanged(check);
    return () => { cancelled = true; off(); };
  }, [result]);

  /** Upload the missing fonts' files (TTF / OTF / WOFF / WOFF2) straight from the import panel. */
  const addFonts = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setFontBusy(true);
    let added = 0;
    for (const f of list) {
      try { await uploadUserFont(f); added += 1; } catch (e) { toast.error(`${f.name}: ${await apiErrorMessage(e, 'could not be uploaded.')}`); }
    }
    setFontBusy(false);
    if (added) toast.success(`${added === 1 ? 'Font added' : `${added} fonts added`} — the import will use ${added === 1 ? 'it' : 'them'} automatically.`);
  };

  const pick = useCallback((f) => {
    if (!f) return;
    if (!/\.(pub|odg)$/i.test(f.name)) { setError('Please choose a Microsoft Publisher file (.pub) or a LibreOffice Draw file (.odg).'); return; }
    const maxMb = caps?.max_file_mb || 100;
    if (f.size > maxMb * 1024 * 1024) {
      // checked here because the proxy in front of the site refuses bigger uploads before our server can explain why
      setFile(null); setResult(null); setErrorRef(null);
      setError(`This file is ${(f.size / (1024 * 1024)).toFixed(0)} MB. Publisher files up to ${maxMb} MB can be imported for now. In Publisher, try File ▸ Info ▸ Compress Pictures (or remove unused pages), save a smaller copy and import that — or export the publication as a PDF.`);
      return;
    }
    setFile(f); setResult(null); setError(null); setErrorRef(null);
  }, [caps]);

  // elapsed-time ticker while a job runs (the server's elapsed_seconds re-syncs it on every poll)
  useEffect(() => {
    if (!job || (job.status !== 'queued' && job.status !== 'running')) return undefined;
    const t = window.setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [job]);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  /** Poll `jobId` to the end, mirroring its status into the panel. Resolves to the final job document. */
  const followJob = async (jobId, kind, fileName) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try { sessionStorage.setItem(RESUME_KEY, JSON.stringify({ jobId, kind, fileName })); } catch { /* ignore */ }
    return waitForConvertJob(jobId, (j) => { setJob(j); if (typeof j.elapsed_seconds === 'number') setElapsed(j.elapsed_seconds); }, { signal: ctrl.signal });
  };
  const clearResume = () => { try { sessionStorage.removeItem(RESUME_KEY); } catch { /* ignore */ } };

  const runImport = async (f = file) => {
    if (!f) return;
    setBusy('import'); setProgress(0); setError(null); setErrorRef(null); setResult(null); setJob(null); setElapsed(0);
    let jobId = null;
    try {
      const started = await startConvertJob(f, 'import', { include_previews: true, preview_dpi: 40 }, setProgress);
      jobId = started.job_id;
      setProgress(100);
      setJob(started);
      await followJob(started.job_id, 'import', f.name);
      const data = await getConvertJobResult(started.job_id);
      setResult(data);
      clearResume();
    } catch (e) {
      if (e?.aborted) return; // cancelled by the user
      setError(await apiErrorMessage(e, 'The file could not be converted.'));
      setErrorRef(refCode(e?.job?.job_id || jobId));
      noteEngineError(e);
      if (e?.job) clearResume();
    } finally { setBusy(null); setJob(null); }
  };

  /** Stop waiting and ask the server to stop the conversion. */
  const cancelJob = async () => {
    const id = job?.job_id;
    abortRef.current?.abort();
    clearResume();
    setBusy(null); setJob(null);
    if (id) { try { await cancelConvertJob(id); } catch { /* it may already be over */ } }
    toast.info('Conversion cancelled.');
  };

  // A conversion started earlier in this tab (the user left the panel and came back) is picked up again.
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem(RESUME_KEY); } catch { /* ignore */ }
    if (!raw) return;
    let saved;
    try { saved = JSON.parse(raw); } catch { clearResume(); return; }
    if (!saved?.jobId) { clearResume(); return; }
    (async () => {
      setBusy(saved.kind === 'import' ? 'import' : saved.kind); setProgress(100); setError(null);
      try {
        const done = await followJob(saved.jobId, saved.kind, saved.fileName);
        if (saved.kind === 'import') {
          setResult(await getConvertJobResult(saved.jobId));
        } else {
          const { blob, name } = await downloadConvertJob(saved.jobId);
          downloadBlob(blob, name || `${safeFileName((saved.fileName || 'document').replace(/\.(pub|odg)$/i, ''))}.${saved.kind}`);
          toast.success(`${String(done.kind || saved.kind).toUpperCase()} download started`);
        }
      } catch (e) {
        if (!e?.aborted && e?.response?.status !== 404) { setError(await apiErrorMessage(e, 'The conversion could not be finished.')); setErrorRef(refCode(saved.jobId)); }
      } finally { clearResume(); setBusy(null); setJob(null); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Fetch the bundled sample (a small newsletter set in fonts this server lacks) and convert it straight away. */
  const trySample = async () => {
    setBusy('sample'); setError(null); setResult(null);
    try {
      const f = await fetchImportSample('missing-fonts');
      setFile(f);
      await runImport(f);
    } catch (e) {
      setBusy(null);
      setError(await apiErrorMessage(e, 'The sample could not be loaded.')); setErrorRef(null);
    }
  };

  const download = async (kind) => {
    if (!file) return;
    setBusy(kind); setError(null); setErrorRef(null); setProgress(0); setJob(null); setElapsed(0);
    let jobId = null;
    try {
      const started = await startConvertJob(file, kind, kind === 'png' ? { dpi: 150 } : {}, setProgress);
      jobId = started.job_id;
      setProgress(100);
      setJob(started);
      await followJob(started.job_id, kind, file.name);
      const { blob, type, name } = await downloadConvertJob(started.job_id);
      downloadBlob(blob, name || `${safeFileName(file.name.replace(/\.(pub|odg)$/i, ''))}${kind === 'pdf' ? '.pdf' : type.includes('zip') ? '-pages.zip' : '.png'}`);
      clearResume();
      toast.success(`${kind.toUpperCase()} download started`);
    } catch (e) {
      if (e?.aborted) return;
      setError(await apiErrorMessage(e, 'The download could not be prepared.'));
      setErrorRef(refCode(e?.job?.job_id || jobId));
      noteEngineError(e);
      if (e?.job) clearResume();
    } finally { setBusy(null); setJob(null); }
  };

  const missingFonts = (fontInfo || []).filter((f) => f.status === 'missing').map((f) => f.family);

  const openInEditor = (replaceFonts = false) => {
    if (!result?.project) return;
    dispatch({ type: 'SET_PROJECT', payload: { ...result.project, id: null } });
    toast.success(`Imported “${result.project.name}” with ${result.project.pages?.length || 0} page(s)`);
    onOpened && onOpened();
    // Straight into Replace Fonts for the families this computer does not have (the toast on load offers the same).
    if (replaceFonts && missingFonts.length) window.setTimeout(() => openDialog('font-substitution', { fonts: missingFonts }), 150);
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); };

  return (
    <div className="space-y-4" data-testid="import-publisher-panel">
      {caps && !engineReady && engineInstalling && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm text-foreground" role="status" aria-live="polite" data-testid="import-engine-preparing">
          <div className="flex items-start gap-2">
            <Cog className="h-4 w-4 mt-0.5 shrink-0 text-primary animate-spin [animation-duration:3s]" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">Preparing the Publisher conversion engine on this server — about a minute, one time only.</div>
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="import-engine-message">{engine.message || 'Starting…'}</div>
              <Progress value={engine.progress != null ? Math.round(engine.progress * 100) : (engine.step === 'downloading' ? 0 : engine.step ? 95 : 0)} className="h-1.5 mt-2" aria-label="Engine install progress" data-testid="import-engine-progress" />
              <div className="text-[11px] text-muted-foreground mt-1">You can pick your file now — Convert unlocks automatically when the engine is ready. LibreOffice Draw (.odg) files import right away.</div>
            </div>
          </div>
        </div>
      )}
      {caps && !engineReady && !engineInstalling && (
        <div className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-900 dark:text-amber-100" data-testid="import-capability-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="font-medium">{engine?.state === 'failed' ? 'The Publisher conversion engine could not be prepared on this server.' : 'The Publisher conversion engine (LibreOffice) is not available on this server.'}</div>
            {engine?.error && <div className="text-xs mt-0.5 break-words" data-testid="import-engine-error">{engine.error}</div>}
            <div className="text-xs mt-1">LibreOffice Draw (.odg) files can still be imported. For .pub files, try again — the engine is downloaded automatically.</div>
          </div>
          <Button size="sm" variant="outline" onClick={retryEngine} className="shrink-0 h-8 border-amber-500/50 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40" data-testid="import-engine-retry"><RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Retry</Button>
        </div>
      )}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-muted/40'}`}
        data-testid="import-drop-zone"
      >
        <input ref={inputRef} type="file" accept=".pub,.odg,application/x-mspublisher,application/vnd.oasis.opendocument.graphics" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="import-file-input" />
        <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
        <div className="mt-2 text-sm font-medium text-foreground">{file ? file.name : 'Drop a Publisher file here or click to browse'}</div>
        <div className="text-xs text-muted-foreground mt-1">{file ? `${(file.size / 1024).toFixed(0)} KB` : `Supports Publisher 2003 and newer (.pub) and LibreOffice Draw (.odg) up to ${caps?.max_file_mb || 100} MB (for now)`}</div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => runImport()} disabled={!file || !!busy || blocked} data-testid="import-convert-button">{busy === 'import' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderOpen className="h-4 w-4 mr-2" />}Convert &amp; Preview</Button>
        <Button variant="outline" onClick={() => download('pdf')} disabled={!file || !!busy || (caps && !engineReady)} data-testid="import-download-pdf-button">{busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}Download PDF</Button>
        <Button variant="outline" onClick={() => download('png')} disabled={!file || !!busy || (caps && !engineReady)} data-testid="import-download-png-button">{busy === 'png' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileImage className="h-4 w-4 mr-2" />}Download PNG</Button>
        <Button variant="ghost" onClick={trySample} disabled={!!busy || (caps && !engineReady)} className="ml-auto text-muted-foreground" title="Convert a bundled one-page newsletter set in Bookman Old Style, Tw Cen MT and Lucida Handwriting — fonts this server does not have — to try Replace Fonts on an import" data-testid="import-try-sample-button">{busy === 'sample' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Type className="h-4 w-4 mr-2" />}Try a sample with missing fonts</Button>
      </div>

      {busy && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2" role="status" aria-live="polite" data-testid="import-progress">
          {progress < 100 && !job ? (
            <>
              <Progress value={progress} className="h-2" aria-label="Upload progress" />
              <div className="text-xs text-muted-foreground" data-testid="import-progress-message">Uploading… {progress}%</div>
            </>
          ) : (
            <>
              <Progress value={job?.progress != null ? Math.max(3, Math.round(job.progress * 100)) : 3} className="h-2" aria-label="Conversion progress" />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="flex items-center gap-1.5 text-foreground" data-testid="import-progress-message"><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />{job?.message || (busy === 'import' || busy === 'sample' ? 'Starting the conversion…' : `Preparing the ${String(busy).toUpperCase()}…`)}</span>
                <span className="flex items-center gap-1 text-muted-foreground tabular-nums" data-testid="import-progress-elapsed"><Clock className="h-3 w-3" aria-hidden="true" />{fmtElapsed(elapsed)}</span>
                {job?.job_id && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs ml-auto" onClick={cancelJob} data-testid="import-cancel-button"><XCircle className="h-3.5 w-3.5 mr-1" aria-hidden="true" />Cancel</Button>}
              </div>
              <div className="text-[11px] text-muted-foreground" data-testid="import-progress-hint">{job?.status === 'queued' ? 'Another conversion is running on the server — yours starts as soon as it finishes.' : `Large publications with many pictures can take several minutes${job?.timeout_seconds ? ` (this one is allowed up to ${Math.max(1, Math.round(job.timeout_seconds / 60))} minutes)` : ''}. The conversion keeps running on the server if you close this screen — come back here to pick it up.`}</div>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground" role="alert" data-testid="import-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
          <div className="min-w-0">
            <div className="font-medium">Import failed</div>
            <div className="text-muted-foreground break-words">{error}</div>
            {errorRef && <div className="text-[11px] text-muted-foreground mt-1" data-testid="import-error-reference">Reference <span className="font-mono text-foreground/80">{errorRef}</span> — quote it if you ask for help; it points support to this conversion.</div>}
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-md border border-border bg-card p-3 space-y-3" data-testid="import-result">
          <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="font-medium">{result.project?.name}</span><span className="text-muted-foreground">— {result.project?.pages?.length || 0} page(s), {result.stats?.objects ?? result.project?.pages?.reduce((s, p) => s + (p.objects?.length || 0), 0)} object(s)</span>{result.stats?.pictures_optimised > 0 && <span className="text-xs text-muted-foreground ml-1" data-testid="import-pictures-note">· {result.stats.pictures_optimised} picture{result.stats.pictures_optimised === 1 ? '' : 's'} sized for the editor</span>}</div>
          {result.previews?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="import-previews">
              {result.previews.map((src, i) => <img key={`page-${i + 1}`} src={src} alt={`Page ${i + 1}`} className="h-28 rounded-sm border border-border bg-white shrink-0" />)}
            </div>
          )}
          {result.fonts?.length > 0 && (
            <div className="text-xs text-muted-foreground space-y-1.5" data-testid="import-fonts-used">
              <div><span className="font-medium text-foreground">Fonts used:</span> {result.fonts.join(', ')}</div>
              {fontInfo && fontInfo.some((f) => f.status === 'missing') ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 p-2 text-amber-900 dark:text-amber-100" data-testid="import-fonts-missing">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span className="flex-1 min-w-[12rem]"><span className="font-medium">Not available here:</span> {fontInfo.filter((f) => f.status === 'missing').map((f) => f.family).join(', ')} — these will be shown in a substitute face. Add the font files (from your Windows Fonts folder) and the import uses them automatically.</span>
                  <input ref={fontInputRef} type="file" accept=".ttf,.otf,.woff,.woff2" multiple className="hidden" onChange={(e) => { addFonts(e.target.files); e.target.value = ''; }} data-testid="import-font-upload-input" />
                  <Button size="sm" variant="outline" className="h-7 text-xs bg-background" disabled={fontBusy} onClick={() => fontInputRef.current?.click()} data-testid="import-font-upload-button">{fontBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Type className="h-3.5 w-3.5 mr-1.5" />} Add font files…</Button>
                </div>
              ) : fontInfo ? (
                <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300" data-testid="import-fonts-ok"><CheckCircle2 className="h-3.5 w-3.5" /> {(() => {
                  const twins = fontInfo.filter((f) => f.status === 'twin').length;
                  const users = fontInfo.some((f) => f.status === 'user') ? ' (including your uploaded fonts)' : '';
                  if (!twins) return `All fonts are available${users}.`;
                  return twins === fontInfo.length ? `Every font is covered: ${twins === 1 ? 'it is' : `all ${twins} are`} shown with a free look-alike (below).` : `Every font is covered${users} — ${twins} shown with free look-alikes (below).`;
                })()}</div>
              ) : (
                <div className="flex items-center gap-1.5" data-testid="import-fonts-checking"><Loader2 className="h-3 w-3 animate-spin" /> Checking font availability…</div>
              )}
              {fontInfo && fontInfo.some((f) => f.status === 'twin') && (
                <div className="rounded-md border border-border bg-muted/40 p-2 text-foreground/90" data-testid="import-fonts-twins">
                  <span className="font-medium">Shown with free look-alikes:</span>{' '}
                  {fontInfo.filter((f) => f.status === 'twin').map((f) => `${f.family} → ${f.twin}${f.metric ? ' (same letter widths)' : ''}`).join(' · ')}.{' '}
                  <span className="text-muted-foreground">Microsoft fonts are not installed here; the twins keep the line breaks{fontInfo.some((f) => f.status === 'twin' && !f.metric) ? ' where marked, and come as close as possible otherwise' : ''}. On a PC that has the real fonts they are used instead.</span>
                </div>
              )}
            </div>
          )}
          {result.warnings?.length > 0 && (
            <details className="text-xs" data-testid="import-warnings">
              <summary className="cursor-pointer text-muted-foreground flex items-center gap-1"><Info className="h-3 w-3" /> {result.warnings.length} note(s) about this import</summary>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-muted-foreground">{result.warnings.map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}</ul>
            </details>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => openInEditor(false)} data-testid="import-open-in-editor-button"><FolderOpen className="h-4 w-4 mr-2" /> Open in Editor</Button>
            {missingFonts.length > 0 && (
              <Button variant="outline" onClick={() => openInEditor(true)} data-testid="import-open-replace-fonts-button"><Replace className="h-4 w-4 mr-2" /> Open & Replace Missing Fonts…</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
